import React, { useState, useEffect } from 'react';
import { Eye, MessageCircle, ArrowRight, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { NewsContent, newsService } from '../../../../services/news';
import DOMPurify from 'dompurify';

export default function NewsGrid({ isHomePage = false }) {
  const [news, setNews] = useState<NewsContent[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const fetchedNews = await newsService.getNews();
        setNews(fetchedNews);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch news');
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const filterednews = news.filter((blog) =>
    blog.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displaynews = isHomePage ? filterednews.slice(0, 3) : filterednews;

  const sanitizeHTML = (html: string, maxLength: number = 100) => {
    // Remove HTML tags
    const strippedText = html.replace(/<[^>]*>/g, '');
    
    // Truncate text
    const truncatedText = strippedText.length > maxLength 
      ? strippedText.substring(0, maxLength) + '...' 
      : strippedText;

    // Sanitize the truncated text
    return DOMPurify.sanitize(truncatedText, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: []
    });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="container mx-auto px-4">
      {!isHomePage && (
        <div className="mb-8">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Cari artikel..."
              className="w-full p-2 pl-10 border rounded-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
          </div>
        </div>
      )}

      {displaynews.length === 0 ? (
        <div className="text-center text-gray-500">
          Untuk sekarang belum ada berita.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displaynews.map((blog) => (
            <div
              key={blog.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105"
            >
              {blog.image && (
                <img
                  className="h-48 w-full object-cover"
                  src={`${process.env.REACT_APP_API_URL}${blog.image}`}
                  alt={blog.title}
                />
              )}
              <div className="p-6">
                <a
                  href="#"
                  className="block mt-2 text-xl font-semibold text-gray-900 hover:text-indigo-600 transition-colors duration-300"
                >
                  {blog.title}
                </a>
                <div 
                  className="mt-3 text-gray-500 text-sm"
                  dangerouslySetInnerHTML={{ 
                    __html: sanitizeHTML(blog.description) 
                  }}
                />
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="flex items-center text-sm text-gray-500 mr-4">
                      <Eye size={16} className="mr-1" /> 0
                    </span>
                    <span className="flex items-center text-sm text-gray-500">
                      <MessageCircle size={16} className="mr-1" /> 0
                    </span>
                  </div>
                  <Link
                    to={`/berita/${blog.id}`}
                    className="text-indigo-600 hover:text-indigo-800 transition-colors duration-300 flex items-center"
                  >
                    Selengkapnya <ArrowRight size={16} className="ml-1" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}